import {
  ComponentPropsWithoutRef,
  ReactNode,
  memo,
  useEffect,
  useRef,
} from 'react'
import { IntlShape, useIntl } from 'react-intl'
import { styled } from '@mui/material/styles'
import { Avatar } from '@mui/material'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined'
import ReactMarkdown, { uriTransformer } from 'react-markdown'
import type { ReactMarkdownProps } from 'react-markdown/lib/ast-to-react'
import remarkBreaks from 'remark-breaks'
import remarkGfm from 'remark-gfm'
import { colorPalette } from '../../style/colorPalette'
import { AiAgentChatMessage } from './hooks/useAiAgentConversation'
import { MessageSegment, segmentsToText } from './tokenChunks'
import { WbsItemAttachmentChip } from './WbsItemAttachmentChip'
import { WbsItemLinkChip } from './WbsItemLinkChip'
import { WbsItemStatusLinkChip } from './WbsItemStatusLinkChip'
import { UserLinkChip } from './UserLinkChip'
import { parseWbsItemLinkHref } from './wbsLinkHref'
import { parseWbsItemStatusLinkHref } from './wbsStatusLinkHref'
import { parseUserLinkHref } from './userLinkHref'

// 送信待ちが増えても会話が読める最低限の高さ。ここまで縮んだ後は送信待ち側がスクロールする。
const MIN_HISTORY_HEIGHT = 100

// flex の縮小は各要素のサイズ比で配分されるため、既定のままだと会話と送信待ちが同時に縮む。
// 送信待ちのカードは全部見えていてほしいので、会話側の縮みやすさを大きくして先に詰めさせる。
const HISTORY_SHRINK_PRIORITY = 100

const HistoryArea = styled('div')({
  flexGrow: 1,
  flexShrink: HISTORY_SHRINK_PRIORITY,
  minHeight: MIN_HISTORY_HEIGHT,
  overflowY: 'auto',
  padding: 16,
  backgroundColor: 'transparent',
})

const MessageContainer = styled('div', {
  shouldForwardProp: prop => prop !== 'isUser',
})<{ isUser: boolean }>(({ isUser }) => ({
  display: 'flex',
  justifyContent: isUser ? 'flex-end' : 'flex-start',
  alignItems: 'flex-start',
  marginBottom: 8,
}))

// 1 メッセージを構成するブロックを縦に積む列。アシスタント応答はツール呼び出しごとに
// 吹き出しが分割され、ツールのチップは吹き出しの外側の独立したブロックになる。
const MessageBlocks = styled('div', {
  shouldForwardProp: prop => prop !== 'isUser',
})<{ isUser: boolean }>(({ isUser }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: isUser ? 'flex-end' : 'flex-start',
  gap: 6,
  maxWidth: '80%',
  // flex アイテムの min-width は既定が auto で、中身の最小コンテンツ幅が下限になる。
  // 折り返せないチップ（WbsItemLinkChip 等は nowrap）があるとその全長が下限となり、
  // min-width が max-width より優先されるため maxWidth: '80%' が無効化されてしまう。
  minWidth: 0,
  marginLeft: isUser ? 0 : 8,
  marginRight: isUser ? 8 : 0,
}))

const MessageBubble = styled('div', {
  shouldForwardProp: prop => prop !== 'isUser' && prop !== 'isError',
})<{
  isUser: boolean
  isError?: boolean
}>(({ isUser, isError }) => ({
  backgroundColor: isError
    ? colorPalette.pink[1]
    : isUser
      ? colorPalette.skyBlue[1]
      : colorPalette.monotone[1],
  color: isError ? colorPalette.pink[7] : colorPalette.monotone[10],
  borderRadius: 12,
  padding: '8px 12px',
  maxWidth: '100%',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  fontSize: 14,
}))

// 表の列が中身の幅ぴったりまで縮むと、短い値の列が窮屈に潰れて読みにくくなるため下限を設ける。
// はみ出した分は TableScrollArea の横スクロールで追える。
const TABLE_COLUMN_MIN_WIDTH = 150

// マークダウンをチャットバブル内で表示するためのスタイル。
// 先頭・末尾要素の余白を詰めてバブルの padding と重複させない。
const MarkdownContent = styled('div')({
  // バブル側の pre-wrap を打ち消す（マークダウンはブロック要素で改行を表現するため）
  whiteSpace: 'normal',
  '& > *:first-of-type': {
    marginTop: 0,
  },
  '& > *:last-child': {
    marginBottom: 0,
  },
  '& p': {
    margin: '0 0 8px 0',
  },
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    margin: '12px 0 6px 0',
    fontWeight: 600,
    fontSize: 15,
  },
  '& ul, & ol': {
    margin: '0 0 8px 0',
    paddingLeft: 20,
  },
  '& li': {
    marginBottom: 2,
  },
  '& blockquote': {
    margin: '8px 0',
    padding: '0 12px',
    borderLeft: `3px solid ${colorPalette.monotone[3]}`,
    color: colorPalette.monotone[7],
  },
  '& code': {
    padding: '1px 4px',
    fontSize: '85%',
    backgroundColor: colorPalette.monotone[2],
    borderRadius: 3,
    fontFamily:
      'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
  },
  '& pre': {
    margin: '8px 0',
    padding: 12,
    backgroundColor: colorPalette.monotone[2],
    borderRadius: 6,
    overflow: 'auto',
    '& code': {
      padding: 0,
      backgroundColor: 'transparent',
    },
  },
  '& table': {
    borderCollapse: 'collapse',
    margin: '8px 0',
    // 幅を指定しないと表は中身の幅で止まり、パネルを広げても広がらない。100% にして
    // 吹き出しの幅に追従させ、余った幅は列の内容量に応じて配分させる（table-layout: auto）。
    width: '100%',
    '& th, & td': {
      border: `1px solid ${colorPalette.monotone[3]}`,
      padding: '4px 10px',
      minWidth: TABLE_COLUMN_MIN_WIDTH,
    },
    '& th': {
      backgroundColor: colorPalette.monotone[2],
      fontWeight: 600,
    },
  },
  // :not([data-wbs-item-link-chip]) で WbsItemLinkChip を除外する。チップは自前の見た目
  // （白地に枠のバッジ）を持つため、ここの汎用リンク色・下線を継承すると崩れてしまう。
  '& a:not([data-wbs-item-link-chip])': {
    color: colorPalette.skyBlue[7],
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  '& img': {
    maxWidth: '100%',
    height: 'auto',
  },
})

// 表は列幅を中身に合わせて広げる（table-layout: auto）ため、長いチップや長文のセルがあると
// 吹き出しの幅を超えて右へはみ出す。横スクロールできる入れ物に入れて受け止める。
// 列幅の均等割り（table-layout: fixed）にしないのは、列数が多いと全列が一様に狭くなり、
// 短い値の列にも同じ幅が割り当てられて読みにくくなるため。
const TableScrollArea = styled('div')({
  maxWidth: '100%',
  overflowX: 'auto',
})

// ユーザーが送信時に添付した画像のサムネイル一覧。
const UserImagesRow = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 6,
  marginBottom: 6,
})

const UserImageThumb = styled('img')({
  maxWidth: 160,
  maxHeight: 160,
  borderRadius: 8,
  objectFit: 'cover',
})

// ユーザーが送信時に添付したWBSアイテムの一覧。何を渡したかが見えるよう、送信済みバブルにも
// 常に表示する（画像添付と同じ位置づけ）。
const UserWbsItemAttachmentsRow = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 6,
  marginBottom: 6,
})

const AiAvatar = styled(Avatar)({
  backgroundColor: colorPalette.skyBlue[7],
  width: 32,
  height: 32,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})

const LoadingBubble = styled('div')({
  display: 'inline-block',
  width: '16px',
  height: '16px',
  border: `2px solid ${colorPalette.monotone[2]}`,
  borderTop: `2px solid ${colorPalette.skyBlue[7]}`,
  borderRadius: '50%',
  animation: 'spin 1s linear infinite',
  '@keyframes spin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
})

// エージェントがツールを実行したことを示すチップ。吹き出しの外側に置かれ、
// 複数回の呼び出しは MessageBlocks の縦積みでそのまま 1 行ずつ並ぶ。
//
// このチップは実行中のツールにしか出ない（renderAssistantBlocks 参照）ため、
// グレーの塗り（UI 上は非活性の記号）ではなく AiAvatar / LoadingBubble と同じ
// skyBlue 系で「いま動いている」ことを示す。
// 新しいツールへ切り替わるたびに要素が差し替わるので、出現アニメーションが
// 「次の操作に進んだ」ことの手掛かりになる（同じ文言が続く探索系ツールでも伝わる）。
//
// 処理中であることは、文字ではなくチップの背景をハイライトが左から右へ走ること（シマー）で示す。
// 文字自体を塗り替えると、ハイライトが通過している間だけ 12px の文言のコントラストが落ちるため、
// 可読性に影響しない背景側で動かす。
const ToolCallChip = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 4,
  padding: '2px 8px',
  borderRadius: 10,
  backgroundColor: colorPalette.skyBlue[0],
  border: `1px solid ${colorPalette.skyBlue[2]}`,
  fontSize: 12,
  // 停止位置はグラデーション画像に対する比率で、画像幅は backgroundSize により要素幅の 3 倍になる。
  // 帯の 20% は要素幅の約 60% にあたり、小さなチップ全体をひと撫でするだけの太さになる。
  // ハイライトの skyBlue[2] 上でも文字（skyBlue[10]）は 5.2:1 あり、WCAG AA を保てる。
  backgroundImage: `linear-gradient(100deg, ${colorPalette.skyBlue[0]} 40%, ${colorPalette.skyBlue[2]} 50%, ${colorPalette.skyBlue[0]} 60%)`,
  backgroundSize: '300% 100%',
  animation:
    'toolCallAppear 180ms ease-out, toolCallShimmer 1.8s linear infinite',
  '@keyframes toolCallAppear': {
    from: { opacity: 0, transform: 'translateY(4px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
  // background-position を 100% -> 0% と減らすと背景は右へ動く＝ハイライトが左から右へ走る。
  '@keyframes toolCallShimmer': {
    from: { backgroundPosition: '100% 0' },
    to: { backgroundPosition: '0% 0' },
  },
  '& svg': {
    fontSize: 14,
    color: colorPalette.skyBlue[7],
    // アイコンは 1 行目の文字と高さを揃える（引数付きの文言は複数行になりうる）
    marginTop: 2,
    flexShrink: 0,
  },
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
    // 止めるとハイライトが途中の位置で固定され塗りが不均一に見えるため、
    // グラデーション自体をやめて backgroundColor の単色に戻す。
    backgroundImage: 'none',
  },
})

// 引数付きの文言は長くなりうるため 2 行で打ち切る。全文は title 属性で参照できる。
const ToolCallText = styled('span')({
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
  overflow: 'hidden',
  wordBreak: 'break-word',
  color: colorPalette.skyBlue[10],
})

// ツール名の接頭辞ごとのアイコン。いま文言を用意しているツール（i18n の aiAgentChat.tool.*）は
// search_ / get_ / create_ / update_ / move_ の接頭辞で操作の種類が判別できる。個々のツール名で
// 持たないのは、サーバ側にツールが追加されても分類だけで妥当なアイコンに解決できるようにするため。
// ここに無い接頭辞（save_ / add_ / change_ 等）は下のフォールバックで歯車になる。
const TOOL_ICON_BY_PREFIX = [
  { prefix: 'search_', Icon: SearchOutlinedIcon },
  { prefix: 'get_', Icon: DescriptionOutlinedIcon },
  { prefix: 'create_', Icon: AddCircleOutlineIcon },
  { prefix: 'update_', Icon: EditOutlinedIcon },
  { prefix: 'move_', Icon: DriveFileMoveOutlinedIcon },
] as const

// どの系統にも当てはまらないツールは汎用の歯車で「ツールを実行した」ことだけを伝える。
const resolveToolIcon = (toolName: string) =>
  TOOL_ICON_BY_PREFIX.find(({ prefix }) => toolName.startsWith(prefix))?.Icon ??
  SettingsOutlinedIcon

// 引数名を表示用のラベルに解決する。未知の引数名はそのまま表示して手掛かりを残す。
const resolveArgLabel = (intl: IntlShape, key: string) => {
  const messageId = `aiAgentChat.toolArg.${key}`
  return intl.messages[messageId] ? intl.formatMessage({ id: messageId }) : key
}

// ツール名と引数を表示用の文言に解決する。未知のツールでも実行された事実は伝える。
const ToolCallSegment = ({
  toolName,
  toolInput,
}: {
  toolName: string
  toolInput: Record<string, string>
}) => {
  const intl = useIntl()
  const messageId = `aiAgentChat.tool.${toolName}`
  const label = intl.messages[messageId]
    ? intl.formatMessage({ id: messageId })
    : intl.formatMessage({ id: 'aiAgentChat.tool.unknown' }, { toolName })
  // 引数はエージェントが指定した順序で届く（先頭が必須引数=操作対象になりやすい）ため、そのまま並べる
  const args = Object.entries(toolInput)
    .map(([key, value]) => `${resolveArgLabel(intl, key)}: ${value}`)
    .join(', ')
  const text = args
    ? intl.formatMessage({ id: 'aiAgentChat.tool.withArgs' }, { label, args })
    : label
  const ToolIcon = resolveToolIcon(toolName)
  return (
    <ToolCallChip title={text}>
      <ToolIcon />
      <ToolCallText>{text}</ToolCallText>
    </ToolCallChip>
  )
}

// react-markdown の既定の transformLinkUri は http/https/mailto/tel 等以外のスキームを
// javascript:void(0) に潰してしまう（unsafe な URL を a コンポーネントに渡さないための挙動）。
// wbs: / status: / user: はこの許可リストに無いため、有効な独自スキームのリンクだけ変換を
// スキップして components.a まで元の値を届ける。それ以外は既定のサニタイズに委ねる。
const transformLinkUri = (uri: string) =>
  parseWbsItemLinkHref(uri) ||
  parseWbsItemStatusLinkHref(uri) ||
  parseUserLinkHref(uri)
    ? uri
    : uriTransformer(uri)

// リンクテキストは通常 remark が渡す文字列 1 要素だが、念のため配列（太字等が混ざる場合）も
// 素朴に連結して拾う。書式は落ちるが、チップの表示名としては文字が取れれば十分。
const linkChildrenToText = (children: ReactNode): string => {
  if (typeof children === 'string') {
    return children
  }
  if (Array.isArray(children)) {
    return children
      .map(child => (typeof child === 'string' ? child : ''))
      .join('')
  }
  return ''
}

// 応答本文の `[名称](wbs:コード)` / `[Done](status:DONE)` / `[氏名](user:コード)` を
// チップに差し替える。
//
// linkTarget="_blank" は react-markdown v8 では既定の a レンダラにしか効かず、components.a を
// 渡すと無視される仕様のため、wbs: 以外の通常リンクはここで target/rel を明示して既存の
// 「新しいタブで開く」挙動を維持する（忘れると外部リンクが同一タブで開く回帰になる）。
const MarkdownLink = ({
  href,
  children,
  // react-markdown が付与する node/sourcePosition/index/siblingCount は DOM の a 要素には
  // 存在しない属性のため、素の <a> へそのまま渡さないよう分割代入で取り除く。
  node,
  sourcePosition,
  index,
  siblingCount,
  ...rest
}: ComponentPropsWithoutRef<'a'> & ReactMarkdownProps) => {
  const wbsItemCode = href ? parseWbsItemLinkHref(href) : null
  if (wbsItemCode) {
    // リンクテキストが空（`[](wbs:コード)` 等、想定外の入力）のときはコードを表示名の代用にする。
    const label = linkChildrenToText(children).trim() || wbsItemCode
    return <WbsItemLinkChip code={wbsItemCode} label={label} />
  }
  // ステータスはリンクテキストを見ない（表示ラベルは値から引く。wbsStatusLinkHref 参照）。
  const wbsItemStatus = href ? parseWbsItemStatusLinkHref(href) : null
  if (wbsItemStatus) {
    return <WbsItemStatusLinkChip status={wbsItemStatus} />
  }
  const userCode = href ? parseUserLinkHref(href) : null
  if (userCode) {
    // 氏名を取得できるまでの暫定表示に使う。リンクテキストが空（`[](user:コード)` 等、
    // 想定外の入力）のときはコードで代用する。
    const label = linkChildrenToText(children).trim() || userCode
    return <UserLinkChip code={userCode} label={label} />
  }
  // 既定のサニタイズを通らなかった URI は javascript:void(0) に潰されて届く（`wbs:` や
  // `status:` の綴り・値を誤った場合もここに来る）。クリックしても何も起きないリンクを見せる意味はないため、
  // リンクにせず本文の文字として出す。React が javascript: URL に警告を出す件も避けられる。
  if (href?.startsWith('javascript:')) {
    return <>{children}</>
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" {...rest}>
      {children}
    </a>
  )
}

// 表を TableScrollArea で包むだけのレンダラ。react-markdown が付与する node 等は素の
// table 要素には存在しない属性のため、MarkdownLink と同様に分割代入で取り除く。
const MarkdownTable = ({
  children,
  node,
  sourcePosition,
  index,
  siblingCount,
  ...rest
}: ComponentPropsWithoutRef<'table'> & ReactMarkdownProps) => (
  <TableScrollArea>
    <table {...rest}>{children}</table>
  </TableScrollArea>
)

// アシスタント応答をブロック列に組み立てる。ツール呼び出しは吹き出しの外に独立して置くため、
// テキストセグメントごとに吹き出しを分ける。サーバ側の content block の切れ目がそのまま
// 吹き出しの切れ目になる。
//
// ツールのチップは「いま何を操作しているか」を示す進行中の表示なので、末尾のセグメントのときだけ
// 描画する。次のツールへ進めば表示は差し替わり、本文テキストが始まれば消えて吹き出しに切り替わる。
// 階層探索のように同じツールが何十回も呼ばれても 1 行を占めるだけで済む。
// セグメント列自体は保持したまま描画側で間引くことで、index キーが安定し再マウントを避けられる。
const renderAssistantBlocks = (segments: MessageSegment[]) =>
  // セグメントは追記のみで並び替え・途中削除が発生しないため、index キーで安全。
  segments.map((segment, index) => {
    if (segment.kind === 'tool') {
      if (index !== segments.length - 1) {
        return null
      }
      return (
        <ToolCallSegment
          key={`segment-${index}`}
          toolName={segment.toolName}
          toolInput={segment.toolInput}
        />
      )
    }
    return (
      <MessageBubble key={`segment-${index}`} isUser={false}>
        <MarkdownContent>
          {/* remarkBreaks で改行1つを <br> にする。CommonMark の soft break は HTML 上ただの
              改行文字にしかならず、MarkdownContent の whiteSpace: normal に畳まれて消えるため、
              エージェントが改行だけで区切った行（箇条書きにしない列挙など）が繋がってしまう。
              components.a を渡すと linkTarget は効かなくなる（MarkdownLink 側で target を明示）。 */}
          <ReactMarkdown
            remarkPlugins={[remarkGfm, remarkBreaks]}
            transformLinkUri={transformLinkUri}
            components={{ a: MarkdownLink, table: MarkdownTable }}
          >
            {segment.text}
          </ReactMarkdown>
        </MarkdownContent>
      </MessageBubble>
    )
  })

// アシスタントの応答のみマークダウンとして解釈する。
// ユーザー入力・エラーはプレーンテキスト（バブルの pre-wrap で改行を保持）のまま。
const renderMessageBlocks = (
  message: AiAgentChatMessage,
  isUser: boolean,
  showLoading: boolean
) => {
  if (showLoading) {
    return (
      <MessageBubble isUser={false}>
        <LoadingBubble />
      </MessageBubble>
    )
  }
  if (isUser || message.isError) {
    return (
      <MessageBubble isUser={isUser} isError={message.isError}>
        {message.images && message.images.length > 0 && (
          <UserImagesRow>
            {message.images.map((image, index) => (
              <UserImageThumb
                key={`${image.url}-${index}`}
                src={image.url}
                alt=""
              />
            ))}
          </UserImagesRow>
        )}
        {message.wbsItemAttachments &&
          message.wbsItemAttachments.length > 0 && (
            <UserWbsItemAttachmentsRow>
              {message.wbsItemAttachments.map(wbsItem => (
                <WbsItemAttachmentChip key={wbsItem.code} wbsItem={wbsItem} />
              ))}
            </UserWbsItemAttachmentsRow>
          )}
        {segmentsToText(message.segments)}
      </MessageBubble>
    )
  }
  return renderAssistantBlocks(message.segments)
}

type AiAgentMessageListProps = {
  messages: AiAgentChatMessage[]
  isStreaming: boolean
  className?: string
}

const AiAgentMessageListComponent = ({
  messages,
  isStreaming,
  className,
}: AiAgentMessageListProps) => {
  const historyRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (historyRef.current) {
      historyRef.current.scrollTop = historyRef.current.scrollHeight
    }
  }, [messages])

  return (
    <HistoryArea ref={historyRef} className={className}>
      {messages.map((message, index) => {
        const isUser = message.role === 'user'
        const showLoading =
          message.role === 'assistant' &&
          !message.complete &&
          message.segments.length === 0 &&
          isStreaming
        return (
          // 会話履歴は追記のみで並び替え・途中削除が発生しないため、index キーで安全。
          <MessageContainer key={`message-${index}`} isUser={isUser}>
            {!isUser && (
              <AiAvatar>
                <SmartToyIcon fontSize="small" />
              </AiAvatar>
            )}
            <MessageBlocks isUser={isUser}>
              {renderMessageBlocks(message, isUser, showLoading)}
            </MessageBlocks>
          </MessageContainer>
        )
      })}
    </HistoryArea>
  )
}

// パネルをドラッグしている間、親は毎フレーム再描画される。会話履歴の描画は重いため、
// 内容が変わっていないときは描画をスキップする。
export const AiAgentMessageList = memo(AiAgentMessageListComponent)
